import { SOCIAL_AD_TYPES } from 'utils/constants';
import { ADTYPE_LABELS, PAC_FORM_TYPES, PAC_TYPES } from 'social/constants';
import { adTypePrettyName } from 'social/utils';
import { getNewClientID } from 'utils/common';

function defaultCreativeName(adType, formValues) {
	return `${formValues.conceptName || ""} - ${adTypePrettyName(adType)}`;
}

export function defaultInitializer(adType, isDynamic=false){
	// By default, we just initialize the Creative Name field based on the concept name and adType
	return function(formValues, formMeta){
		const values = formValues[adType] || {};
		const meta = formMeta[adType] || {};

		if (!meta.name?.touched) {
			values.name = defaultCreativeName(adType, formValues);
		}

		if(isDynamic && !meta.assetType?.touched){
			values.assetType = "prebuilt";
		}

		return values;
	};
}

export function defaultCarouselInitializer(adType, isDynamic=false){
	return function(formValues, formMeta){
		const values = defaultInitializer(adType, isDynamic)(formValues, formMeta);
		const meta = formMeta[adType] || {};

		values.currentCard = 0;

		if(!(meta.cards?.length)){
			values.cards = [
				{
					type: "image",
					order: 0,
					clientId: getNewClientID(),
				},
				{
					type: "image",
					order: 1,
					clientId: getNewClientID(),
				}
			];
		}

		return values;
	};
}

export function customPlatformInitializer(adType, platformName, isDynamic=false){
	/*
		This is the same as the default initialization for the Creative Name, but allows you
		to specify whatever you want for the platform name.
	*/
	return function(formValues, formMeta){
		const values = formValues[adType] || {};
		const meta = formMeta[adType] || {};

		if (!meta.name?.touched) {
			values.name = `${formValues.conceptName || ""} - ${platformName} ${ADTYPE_LABELS[adType]}`;
		}

		if(isDynamic && !meta.assetType?.touched){
			values.assetType = "prebuilt";
		}

		return values;
	};
}

export function facebookImageInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;
	const values = defaultInitializer(adType)(formValues, formMeta);

	if (!values.placementAssetCustomizationType) {
		values.placementAssetCustomizationType = PAC_TYPES.NONE;
	}

	values._currentActivePAC = PAC_FORM_TYPES.DEFAULT;

	return values;
}

export function facebookVideoInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.FACEBOOK_VIDEO;
	const values = defaultInitializer(adType)(formValues, formMeta);

	if (!values.placementAssetCustomizationType) {
		values.placementAssetCustomizationType = PAC_TYPES.NONE;
	}

	values._currentActivePAC = PAC_FORM_TYPES.DEFAULT;

	return values;
}

export function facebookCarouselInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL;
	const values = defaultCarouselInitializer(adType)(formValues, formMeta);

	const meta = formMeta[adType] || {};

	if(!meta.seeMoreCard?.touched){
		values.seeMoreCard = false;
	}

	return values;
}

export function facebookImageMOInitializer(formValues, formMeta){
	const fbValues = facebookImageInitializer(formValues, formMeta);
	const fbMOValues = customPlatformInitializer(SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO, "Meta", true)(formValues, formMeta);
	const values = {
		...fbValues,
		...fbMOValues,
	};

	return values;
}

export function facebookVideoMOInitializer(formValues, formMeta){
	const fbValues = facebookVideoInitializer(formValues, formMeta);
	const fbMOValues = customPlatformInitializer(SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO, "Meta", true)(formValues, formMeta);
	const values = {
		...fbValues,
		...fbMOValues,
	};

	return values;
}

export function facebookCarouselMOInitializer(formValues, formMeta){
	const adType = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO;
	const values = customPlatformInitializer(SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO, "Meta", true)(formValues, formMeta);

	const meta = formMeta[adType] || {};

	values.currentCard = 0;

	if(!(meta.cards?.length)){
		values.cards = [
			{
				type: "image",
				order: 0,
				clientId: getNewClientID(),
			},
			{
				type: "image",
				order: 1,
				clientId: getNewClientID(),
			}
		];
	}

	if(!meta.seeMoreCard?.touched){
		values.seeMoreCard = {
			value:false,
			dynVarName:"",
			isDynamic:false,
		};
	}

	return values;
}

export function googleDemandGenCarouselInitializer(formValues, formMeta){
	const adType = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL;

	const values = customPlatformInitializer(adType, "Google")(formValues, formMeta);
	const meta = formMeta[adType] || {};

	values.currentCard = 0;
	values.requiredAspectRatios = values.requiredAspectRatios || { square: true };

	if(!(meta.cards?.length)){
		values.cards = [
			{
				order: 0,
				type: "image",
				clientId: getNewClientID(),
			},
			{
				order: 1,
				type: "image",
				clientId: getNewClientID(),
			}
		];
	}

	return values;
}

export function googleDemandGenImageInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE;

	const values = customPlatformInitializer(adType, "Google")(formValues, formMeta);
	const meta = formMeta[adType] || {};


	if(!meta.headlines?.touched) {
		values.headlines = [null];
	}

	if(!meta.descriptions?.touched){
		values.descriptions = [null];
	}

	return values;
}

export function youTubeDiscoveryInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY;

	const values = formValues[adType] || {};
	const meta = formMeta[adType] || {};

	if (!meta.name?.touched) {
		values.name = defaultCreativeName(adType, formValues);
	}

	if (!meta.videoTitle?.touched) {
		values.videoTitle = defaultCreativeName(adType, formValues);
	}

	return values;
}

export function youTubeInStreamInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.YOUTUBE_INSTREAM;

	const values = formValues[adType] || {};
	const meta = formMeta[adType] || {};

	if (!meta.name?.touched) {
		values.name = defaultCreativeName(adType, formValues);
	}

	if (!meta.videoTitle?.touched) {
		values.videoTitle = defaultCreativeName(adType, formValues);
	}

	if (!meta.videoPrivacyStatus?.touched) {
		values.videoPrivacyStatus = "unlisted";
	}

	if (!meta.madeForKids?.touched) {
		values.madeForKids = false;
	}

	return values;
}

export function twitterImageInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.TWITTER_IMAGE;
	const values = formValues[adType] || {};
	const meta = formMeta[adType] || {};

	if(!meta.name?.touched) {
		values.name = defaultCreativeName(adType, formValues);
	}

	if(!meta.conversationControl?.touched) {
		values.conversationControl = "Everyone";
	}

	return values;
}

const TWITTER_VIDEO_DEFAULT_CONVERSATION_CONTROL = "Everyone";
export function twitterVideoInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.TWITTER_VIDEO;
	const values = formValues[adType] || {};
	const meta = formMeta[adType] || {};

	if (!meta.name?.touched) {
		values.name = defaultCreativeName(adType, formValues);
	}

	if (!meta.conversationControl?.touched) {
		values.conversationControl = TWITTER_VIDEO_DEFAULT_CONVERSATION_CONTROL;
	}

	return values;
}

export function snapchatVideoInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.SNAPCHAT_VIDEO;
	const values = defaultInitializer(adType, true)(formValues, formMeta);
	const meta = formMeta[adType] || {};

	if (!meta.blockPreloading?.touched) {
		values.blockPreloading = {
			value:true,
			dynVarName:"",
			isDynamic:false,
		};
	}

	return values;
}

export function snapchatImageInitializer(formValues, formMeta) {
	const adType = SOCIAL_AD_TYPES.SNAPCHAT_IMAGE;
	const values = defaultInitializer(adType, true)(formValues, formMeta);
	const meta = formMeta[adType] || {};

	if (!meta.blockPreloading?.touched) {
		values.blockPreloading = {
			value:true,
			dynVarName:"",
			isDynamic:false,
		};
	}

	return values;
}

export const INTIIALIZERS_BY_AD_TYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: facebookImageMOInitializer,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: facebookVideoMOInitializer,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: facebookCarouselMOInitializer,

	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: facebookImageInitializer,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: facebookVideoInitializer,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: facebookCarouselInitializer,

	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: googleDemandGenCarouselInitializer,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: googleDemandGenImageInitializer,

	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: snapchatImageInitializer,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: snapchatVideoInitializer,

	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: youTubeDiscoveryInitializer,
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: youTubeInStreamInitializer,

	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: twitterVideoInitializer,
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: twitterImageInitializer,

	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: defaultInitializer(SOCIAL_AD_TYPES.TIKTOK_VIDEO, true),

	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: defaultInitializer(SOCIAL_AD_TYPES.PINTEREST_IMAGE, true),
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: defaultInitializer(SOCIAL_AD_TYPES.PINTEREST_VIDEO, true),
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: defaultInitializer(SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO, true),
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: defaultCarouselInitializer(SOCIAL_AD_TYPES.PINTEREST_CAROUSEL, true),

	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: defaultInitializer(SOCIAL_AD_TYPES.LINKEDIN_IMAGE, true),
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: defaultInitializer(SOCIAL_AD_TYPES.LINKEDIN_VIDEO, true),
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: defaultCarouselInitializer(SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL, true),
};
